import type { FunctionComponent, ReactNode } from 'react';

import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileContent } from '~/ui/core/tile/Tile';

import { TaskTileHeader } from './TaskTileHeader';

/**
 * Shared scaffold for the read-only task tiles (profile-task and work-task): tile chrome, scroll
 * container, and the sticky close header. Edit and create flows keep their own scaffolds because
 * their headers carry form actions and live inside the form's sticky wrapper. The header spans the
 * tile; what follows it keeps a readable width. The `data-task-tile*` attributes let a page animate
 * the tile open from where the task was shown.
 */
export const TaskTileFrame: FunctionComponent<{
    /** Actions at the end of the header, such as editing the task. */
    readonly actions?: ReactNode;
    readonly children: ReactNode;
    readonly comments?: { readonly isOpen: boolean; readonly onToggle: () => void };
    readonly contentAttrs?: Record<string, string>;
    readonly onClose: () => void;
    readonly testId?: string;
}> = ({ actions, children, comments, contentAttrs, onClose, testId }) => (
    <Tile data-task-tile="" {...(testId === undefined ? {} : { 'data-testid': testId })}>
        <ScrollArea
            className="min-w-0 flex-1"
            scrollFade="vertical"
            scrollFadeSize="var(--spacing-4xl)"
            viewportClassName="[&>div]:block!"
        >
            <TileContent className="min-h-full w-full">
                <div
                    className="flex w-full flex-1 flex-col gap-3xl *:not-first:max-w-[800px]"
                    data-task-tile-content=""
                    {...contentAttrs}
                >
                    <TaskTileHeader actions={actions} comments={comments} onClose={onClose} />
                    {children}
                </div>
            </TileContent>
        </ScrollArea>
    </Tile>
);
